import { X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';

export type BadgeTone =
  | 'default'
  | 'secondary'
  | 'outline'
  | 'success'
  | 'warning'
  | 'destructive'
  | 'info'
  | 'brand';

export interface Tag {
  id: string;
  label: string;
  tone?: BadgeTone;
}

export interface TagListProps {
  tags: readonly (Tag | string)[];
  /** Extra tags collapse into a `+N` chip that reveals them on hover/focus. */
  max?: number;
  onRemove?: (tag: Tag) => void;
  tone?: BadgeTone;
  /**
   * `sm` para donde la etiqueta acompaña y no encabeza —la tarjeta del tablero,
   * donde lo que se lee primero es el nombre del lead—. La diferencia es de
   * jerarquía, no de gusto: al mismo tamaño que el nombre, dos etiquetas pesan
   * más que la persona.
   */
  size?: 'sm' | 'md';
  className?: string;
  emptyLabel?: string;
}

const SIZES = {
  sm: 'px-1.5 py-0 text-[11px] font-normal leading-5',
  md: '',
} as const;

const normalize = (tag: Tag | string): Tag =>
  typeof tag === 'string' ? { id: tag, label: tag } : tag;

export function TagList({
  tags,
  max = 3,
  onRemove,
  tone = 'secondary',
  size = 'md',
  className,
  emptyLabel = '—',
}: TagListProps) {
  const all = tags.map(normalize);
  if (all.length === 0) {
    return <span className={cn('text-sm text-muted-foreground', className)}>{emptyLabel}</span>;
  }

  const shown = all.slice(0, max);
  const hidden = all.slice(max);

  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)}>
      {shown.map((tag) => (
        <Badge
          key={tag.id}
          variant={tag.tone ?? tone}
          className={cn(SIZES[size], onRemove && 'pr-1')}
        >
          <span className="truncate">{tag.label}</span>
          {onRemove && (
            <button
              type="button"
              aria-label={`Quitar ${tag.label}`}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(tag);
              }}
              className="ml-1 rounded-sm opacity-60 transition-opacity duration-150 ease-out hover:opacity-100"
            >
              <X className="size-3" />
            </button>
          )}
        </Badge>
      ))}

      {hidden.length > 0 && (
        <HoverCard openDelay={120}>
          <HoverCardTrigger asChild>
            <button
              type="button"
              aria-label={`Ver ${hidden.length} etiquetas más`}
              className="rounded-md text-xs text-muted-foreground transition-colors duration-150 ease-out hover:text-foreground"
            >
              +{hidden.length}
            </button>
          </HoverCardTrigger>
          <HoverCardContent className="w-auto max-w-64">
            <div className="flex flex-wrap gap-1">
              {hidden.map((tag) => (
                <Badge key={tag.id} variant={tag.tone ?? tone}>
                  {tag.label}
                </Badge>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
    </div>
  );
}
